/**
 * Pure pairing-rule costing + replenishment helpers — Feat #2167
 * (G-2166-K-02 / G-2166-K-03). NO React, NO tsx, NO store reads: money math
 * lives in src/lib (check-price-arithmetic gate); components only render the
 * results.
 *
 * Scope honesty (O1 OPEN, CORRECTED): NO pricing mode is chosen — a rule's
 * pricingMode is null until 林芊/Judy rule, and no seed carries
 * `bundled_into_base`, so these helpers currently receive no bundled rules.
 * The `bundled_into_base` math below (Figma 1:23, the board's worked line)
 * is kept DATA-READY only; the other five PairingPricingMode values are
 * enum-only (OPEN Q-K1/K2/K3, see src/types/api/pairingRule.ts) and are
 * deliberately IGNORED here. Costing-chain wiring is OUT of this wave — do
 * not add behaviour without a ruling.
 */

import type { ProductPairingRule } from '@/types/api/pairingRule';

/** 產品成本 record — one unit cost per product id, supplied by the caller. */
export interface PairingCostRecord {
  productId: string;
  /** Unit cost of ONE stock unit of this product (成本, not 售價). */
  unitCost: number;
}

/** One component's share of the parent's bundled unit cost. */
export interface BundledComponentAllocation {
  ruleId: string;
  componentProductId: string;
  componentUnitCost: number;
  parentQty: number;
  componentQty: number;
  /** componentUnitCost × componentQty / parentQty — per ONE parent unit. */
  allocatedCostPerParentUnit: number;
}

export interface BundledComponentCostResult {
  /** Null when no rule qualifies (nothing to bundle). */
  parentProductId: string | null;
  /** 產品成本 of the parent itself; 0 when its cost record is missing. */
  parentUnitCost: number;
  allocations: BundledComponentAllocation[];
  /**
   * 產品成本 + Σ(component cost × componentQty/parentQty) — the Pricing Base
   * input. // Figma 1:23 — 產品成本 + 盒子成本 + 其他成本 → Pricing Base.
   */
  bundledUnitCost: number;
  /** Product ids (parent or component) with NO cost record — never silently 0. */
  missingCostProductIds: string[];
  /** False when any qualifying product lacked a cost record. */
  isComplete: boolean;
}

/**
 * A rule joins the parent's bundled base cost only when it is ACTIVE,
 * MANDATORY, and explicitly priced `bundled_into_base`:
 * - a null pricingMode (O1 OPEN — undecided) never qualifies;
 * - optional components never join the base (they are the ask-the-customer
 *   instruction, Figma 1:129 — priced at SO time, follow-on scope);
 * - every pricing mode is OPEN (Q-K1/K2/K3) and unwired — this predicate is
 *   data-ready plumbing for a future ruling, not live behaviour.
 */
export function qualifiesForBundledBase(rule: ProductPairingRule): boolean {
  return rule.isActive && rule.pairingType === 'mandatory' && rule.pricingMode === 'bundled_into_base';
}

/**
 * Default-mode bundled cost — // Figma 1:23:
 * 產品成本 + Σ(component cost × componentQty/parentQty), per one parent unit.
 * Feeds Pricing Base; the caller (pricing engine / UI) renders the result,
 * it never recomputes it.
 *
 * @param rules All pairing rules of ONE parent product (mixed types/modes
 *   fine — non-qualifying rules are ignored, see qualifiesForBundledBase).
 *   Rules for more than one parent are a caller bug and throw.
 * @param costRecords 產品成本 lookup for the parent AND each component.
 *   A missing record is reported in `missingCostProductIds` (isComplete:
 *   false) — never silently treated as 0-and-complete.
 */
export function bundledComponentCost(
  rules: readonly ProductPairingRule[],
  costRecords: readonly PairingCostRecord[]
): BundledComponentCostResult {
  const qualifying = rules.filter(qualifiesForBundledBase);

  const parents = new Set(qualifying.map((rule) => rule.parentProductId));
  if (parents.size > 1) {
    throw new Error(
      `bundledComponentCost expects rules of a single parent product, got: ${[...parents].join(', ')}`
    );
  }
  const parentProductId = qualifying[0]?.parentProductId ?? null;

  const costByProductId = new Map(costRecords.map((record) => [record.productId, record.unitCost]));
  const missingCostProductIds: string[] = [];

  let parentUnitCost = 0;
  if (parentProductId !== null) {
    const parentCost = costByProductId.get(parentProductId);
    if (parentCost === undefined) {
      missingCostProductIds.push(parentProductId);
    } else {
      parentUnitCost = parentCost;
    }
  }

  const allocations: BundledComponentAllocation[] = [];
  for (const rule of qualifying) {
    const { parentQty, componentQty } = rule.qtyRatio;
    if (!Number.isFinite(parentQty) || parentQty <= 0 || !Number.isFinite(componentQty) || componentQty <= 0) {
      // Write-path validation (mock handler) rejects these; reaching here is
      // corrupt data — fail loud, never divide by zero into a quiet NaN cost.
      throw new RangeError(
        `Pairing rule ${rule.id} has a non-positive qtyRatio (${parentQty}:${componentQty})`
      );
    }
    const componentUnitCost = costByProductId.get(rule.componentProductId);
    if (componentUnitCost === undefined) {
      missingCostProductIds.push(rule.componentProductId);
      continue;
    }
    allocations.push({
      ruleId: rule.id,
      componentProductId: rule.componentProductId,
      componentUnitCost,
      parentQty,
      componentQty,
      allocatedCostPerParentUnit: (componentUnitCost * componentQty) / parentQty,
    });
  }

  const bundledUnitCost = allocations.reduce(
    (sum, allocation) => sum + allocation.allocatedCostPerParentUnit,
    parentUnitCost
  );

  return {
    parentProductId,
    parentUnitCost,
    allocations,
    bundledUnitCost,
    missingCostProductIds,
    isComplete: missingCostProductIds.length === 0,
  };
}

/**
 * 包材補貨提醒 trigger for a COMPONENT product — G-2166-K-03, Figma 1:101,
 * PLAN diamond 2 (engineering answer).
 *
 * Trigger = 現有量 < 補貨點 (`availableQty < reorderPoint`). The 補貨點 is the
 * PRIMARY indicator — the board itself says 補貨點 is 系統會提前算 (Figma 1:6).
 * 安全備貨天數 (safetyStockDays, registry `planning.safetyStockDays`) is the
 * FLOOR INPUT to that pre-computed reorderPoint — it shapes the threshold
 * upstream and is deliberately NOT a second trigger here. 目標水位
 * (`planning.preferredStockLevel`) is the top-up target the follow-on
 * suggestion fills to, not the trigger either. No new Must field — both
 * inputs already exist as registry `planning.*` rows.
 *
 * Boundary: equal-to-reorderPoint does NOT alert (strictly below). A missing
 * or non-finite input returns false — no planning params means no signal,
 * never a fake alert.
 */
export function componentReplenishmentAlert(
  availableQty: number | null | undefined,
  reorderPoint: number | null | undefined
): boolean {
  if (
    availableQty === null ||
    availableQty === undefined ||
    reorderPoint === null ||
    reorderPoint === undefined ||
    !Number.isFinite(availableQty) ||
    !Number.isFinite(reorderPoint)
  ) {
    return false;
  }
  return availableQty < reorderPoint;
}
